import React, { useEffect, useState } from 'react';
import { formatAccuracy, formatCoords } from '../../utils/locationUtils';
import { getCachedLocationName, reverseGeocode } from '../../utils/locationSearchUtils';

export function LocationName({
  lat,
  lng,
  approximate = false,
  accuracy,
  className = '',
  showCoords = true,
  compact = false,
  fallback = 'Waiting for GPS…',
}) {
  const [name, setName] = useState(() => getCachedLocationName(lat, lng));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (lat == null || lng == null) {
      setName(null);
      return undefined;
    }

    const cached = getCachedLocationName(lat, lng);
    if (cached) {
      setName(cached);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    reverseGeocode(lat, lng)
      .then((label) => {
        if (!cancelled) setName(label);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [lat, lng]);

  if (lat == null || lng == null) {
    return <span className={className}>{fallback}</span>;
  }

  const coords = formatCoords(lat, lng);
  if (loading && !name) {
    return (
      <span className={className}>
        {compact ? coords || 'Locating…' : 'Finding exact location…'}
        {!compact && coords ? (
          <span className="block mt-0.5 text-[10px] text-slate-400">{coords}</span>
        ) : null}
      </span>
    );
  }

  // Always show something useful — place name when ready, otherwise coordinates.
  const label = name || coords || fallback;
  const accuracyLabel = formatAccuracy(accuracy);

  return (
    <span className={className}>
      <span className={compact ? '' : 'block font-semibold text-slate-800'}>{label}</span>
      {approximate ? <span className="text-amber-600"> (last known)</span> : null}
      {showCoords && coords && name ? (
        <span className={`text-[10px] text-slate-400 ${compact ? ' ml-1' : 'block mt-0.5'}`}>
          {coords}
          {accuracyLabel ? ` · ${accuracyLabel}` : ''}
        </span>
      ) : null}
      {showCoords && !name && accuracyLabel ? (
        <span className={`text-[10px] text-slate-400 ${compact ? ' ml-1' : 'block mt-0.5'}`}>
          {accuracyLabel}
        </span>
      ) : null}
    </span>
  );
}

export default LocationName;
